/* ============================================================
   Pure store logic — the selectors and state transforms the data layer
   (store/api.tsx) builds on. Keeping them here (framework- and network-free)
   means views are derived and mutations applied by the same pure functions the
   API layer wraps its network I/O around; a future HTTP-seam test harness reuses
   them unchanged.

   Every function is a pure `(state, …args) => result` (time via Date.now()/
   todayStr() is treated as ambient). Transforms return a fresh AppState (never
   mutate the input); a few also return a small extra (e.g. whether a completion
   should celebrate).
   ============================================================ */
import {
  deriveCadenceState,
  type HabitSchedule,
  isDueOn,
  nextChainDay,
  normalizeSchedule,
  presetToRRule,
  scheduledCount,
} from '@habishine/shared';
// the fold the fast-entry parser matches names with — reused here so a tag
// looked up by name in the store and one resolved from typed text agree
import { foldName } from '@habishine/shared';
import { todayStr, addDaysStr, nextWeekendStr } from '../dates';
import { HUE_ORDER, type HueName } from '../hues';
import type { AppState, CaptureState, DayCheckin, Habit, Project, Scope, Tag, Task } from '../types';
import type { ReplanWhere } from './types';

/* ---------- pin helpers ---------- */

/** Drop `id` from every day's pin slot — returns a new map when something
   changed, or the original when `id` was pinned nowhere. */
export function unpinEverywhere(
  pinned: Record<string, string | null>,
  id: string,
): Record<string, string | null> {
  const days = Object.keys(pinned).filter((d) => pinned[d] === id);
  if (!days.length) return pinned;
  const next = { ...pinned };
  for (const d of days) next[d] = null;
  return next;
}

/* ---------- projects ---------- */

/** The project a task is filed under, or null when it's unfiled (or points at a
   project this device hasn't synced yet — treated the same, since an unresolvable
   project can't be rendered either way). */
export function projectForTask(s: AppState, task: Task): Project | null {
  if (!task.projectId) return null;
  return s.projects.find((l) => l.id === task.projectId) ?? null;
}

/**
 * The colour a task actually renders in: its PROJECT's hue when it's filed,
 * its own only when it isn't.
 *
 * Every screen that draws a task should go through this rather than reading
 * `task.hue`. It's what turns a day holding several projects from an arbitrary
 * rainbow into something colour-coded — you can see which rows are Work and
 * which are House Move without reading a word. It also means recolouring a project
 * recolours its tasks, with no write to any task row.
 */
export function hueForTask(s: AppState, task: Task): HueName {
  return projectForTask(s, task)?.hue ?? task.hue;
}

/** Projects for the picker — live and un-archived, in creation order. */
export function activeProjects(s: AppState): Project[] {
  return s.projects.filter((l) => l.archivedAt === null);
}

/** Retired projects, for the index's "Archived" section. */
export function archivedProjects(s: AppState): Project[] {
  return s.projects.filter((l) => l.archivedAt !== null);
}

/** Every task filed under a project, resolved or not — the project surface's contents.
   Unlike the day selectors this keeps done/skipped rows, because a project's
   completed work is part of what you look at a project to see. */
export function tasksInProject(s: AppState, projectId: string): Task[] {
  return s.tasks.filter((t) => t.projectId === projectId);
}

/** A project's progress — `done` of `total`, over its live (non-skipped) tasks.
   Things's project ring, and the calm read of "how is this going": a count, not
   a deadline. Skipped tasks leave the denominator, since deciding not to do
   something shouldn't make a project look unfinished forever. */
export function projectProgress(s: AppState, projectId: string): { done: number; total: number } {
  return scopeProgress(s, { kind: 'project', id: projectId });
}

/** How many un-resolved tasks a project is carrying — the count the index shows. */
export function projectOpenCount(s: AppState, projectId: string): number {
  return tasksInProject(s, projectId).filter((t) => !t.done && !t.skipped).length;
}

/* ---------- tags ---------- */

/** A task's tags, as ids. Always read a task's tags through this rather than
   `task.tagIds` directly: a snapshot written by a build that predates tags has
   no such field, and `undefined.map` on the first render after an upgrade is a
   crash on launch for upgraders only (see the note in store/persist.ts). */
export function tagIdsOf(task: Task): string[] {
  return task.tagIds ?? [];
}

/** The tags on a task, resolved to rows and in the task's own order. Ids the
   device hasn't synced yet simply drop out — an unresolvable tag can't be
   rendered, exactly as with an unresolvable project. */
export function tagsOfTask(s: AppState, task: Task): Tag[] {
  const byId = new Map(s.tags.map((t) => [t.id, t]));
  return tagIdsOf(task).flatMap((id) => {
    const tag = byId.get(id);
    return tag ? [tag] : [];
  });
}

/** Tags for the picker, creation order — the same stable ordering projects use. */
export function activeTags(s: AppState): Tag[] {
  return s.tags;
}

/** A tag by its (case-folded) name, or null. What fast entry resolves an `@`
   token against, and what stops a second tag being created for a name that
   differs only in case. */
export function tagByName(s: AppState, name: string): Tag | null {
  const wanted = foldName(name);
  return s.tags.find((t) => foldName(t.name) === wanted) ?? null;
}

/** Every task carrying a tag, resolved or not — the tag surface's contents.
   Keeps done/skipped rows for the same reason `tasksInProject` does: a tag's
   completed work is part of what you look at it to see. */
export function tasksWithTag(s: AppState, tagId: string): Task[] {
  return s.tasks.filter((t) => tagIdsOf(t).includes(tagId));
}

/** How many un-resolved tasks a tag is carrying — the count the sidebar shows. */
export function tagOpenCount(s: AppState, tagId: string): number {
  return tasksWithTag(s, tagId).filter((t) => !t.done && !t.skipped).length;
}

/* ---------- scope (either axis) ---------- */

/** Every task a scope holds, resolved ones included. The one place the two axes
   are unified: everything downstream (progress, counts, the week's stats, the
   Plan filter) works off this, so adding a third axis later would be one case
   here rather than a branch in every screen. */
export function tasksInScope(s: AppState, scope: Scope): Task[] {
  return scope.kind === 'project' ? tasksInProject(s, scope.id) : tasksWithTag(s, scope.id);
}

/** `done` of `total` over a scope's live (non-skipped) tasks — the header ring.
   Skipped rows leave the denominator: deciding not to do something shouldn't
   make a project or a tag look unfinished forever. */
export function scopeProgress(s: AppState, scope: Scope): { done: number; total: number } {
  const rows = tasksInScope(s, scope).filter((t) => !t.skipped);
  return { done: rows.filter((t) => t.done).length, total: rows.length };
}

/** One project's week, for the SCOPED Review. Deliberately task-only: habits carry
   no project (and shouldn't — see docs/habit-vs-recurring-task.md), so a scoped
   Review drops the habit half entirely rather than showing a consistency number
   the filter doesn't apply to. Half-filtered numbers are worse than absent ones.

   The framing stays the app's: completed work counted, never a burn-down against
   a deadline, and skipped rows leave the denominator so deciding not to do
   something never makes a project read as failing. */
export interface ProjectWeekStats {
  /** tasks in this project completed within the week */
  tasksDone: number;
  /** tasks in this project skipped within the week — shown only when non-zero, and
     framed as a decision rather than a failure */
  tasksSkipped: number;
  /** the project overall: completed and live totals (the header ring) */
  done: number;
  total: number;
  /** still to do — the number the project is actually carrying */
  open: number;
  /** the week's completed rows, most recent first — the "what I got done" project */
  completed: Task[];
}

export function scopeWeekStats(s: AppState, weekStart: string, scope: Scope): ProjectWeekStats {
  const daySet = new Set(weekDays(weekStart));
  const rows = tasksInScope(s, scope);
  const live = rows.filter((t) => !t.skipped);
  const completed = rows
    .filter((t) => t.done && t.doneAt != null && daySet.has(t.doneAt))
    .sort((a, b) => b.lastTouched - a.lastTouched);
  return {
    tasksDone: completed.length,
    tasksSkipped: rows.filter((t) => t.skipped && t.skippedAt != null && daySet.has(t.skippedAt))
      .length,
    done: live.filter((t) => t.done).length,
    total: live.length,
    open: rows.filter((t) => !t.done && !t.skipped).length,
    completed,
  };
}

/**
 * The colour to offer for the NEXT project: the first palette hue no live project is
 * already using, falling back to the least-used once all seven are taken.
 *
 * Not cosmetic. Projects own the colour their tasks inherit, so it's the whole
 * mechanism by which a mixed day reads as colour-coded — and defaulting every
 * new project to the same hue quietly destroys that, since two projects that look
 * identical carry no information at all. The user can still recolour; this only
 * decides what they start from.
 */
export function nextProjectHue(s: AppState): HueName {
  const used = new Map<HueName, number>();
  for (const l of s.projects) {
    if (l.archivedAt !== null) continue;
    used.set(l.hue, (used.get(l.hue) ?? 0) + 1);
  }
  const free = HUE_ORDER.find((h) => !used.has(h));
  if (free) return free;
  return HUE_ORDER.reduce((best, h) => ((used.get(h) ?? 0) < (used.get(best) ?? 0) ? h : best), HUE_ORDER[0]!);
}

/**
 * The colour to assign the NEXT tag: the first palette hue no live tag is using,
 * falling back to the least-used once all seven are taken. `nextProjectHue`'s
 * rule, over the other axis.
 *
 * The two counts are kept SEPARATE rather than pooled. A tag and a project are
 * never drawn in the same place — a project paints the task row it owns, a tag
 * paints only itself — so a tag sharing a project's hue costs nothing, while
 * pooling would exhaust a seven-colour palette twice as fast and start handing
 * out duplicates within an axis, which is the one collision that does cost
 * something.
 */
export function nextTagHue(s: AppState): HueName {
  return hueForNewTag(s.tags);
}

/** The same rule over a bare list, so a prop-driven component (the capture sheet,
   which takes its tags as a prop) can PREVIEW the colour a new tag will get
   without reaching into the store. `createTag` is what actually assigns it. */
export function hueForNewTag(tags: Tag[]): HueName {
  const used = new Map<HueName, number>();
  for (const t of tags) used.set(t.hue, (used.get(t.hue) ?? 0) + 1);
  const free = HUE_ORDER.find((h) => !used.has(h));
  if (free) return free;
  return HUE_ORDER.reduce((best, h) => ((used.get(h) ?? 0) < (used.get(best) ?? 0) ? h : best), HUE_ORDER[0]!);
}

/* ---------- manual ordering ----------
   The app's priority mechanism. See docs/task-ordering.md for why this is a
   position rather than a P1/P2/P3 field, and why the rank is fractional. */

/** Spacing between ranks when a list is (re)numbered from scratch. */
export const RANK_STEP = 1024;

/** The smallest gap we'll split. Below this, averaging is close enough to the
   double's resolution that a few more inserts would collapse two ranks onto one
   value — so the caller renumbers instead.

   With `RANK_STEP` at 1024 this allows ~30 successive drops into the SAME gap
   before the fallback fires (1024 / 2^30 ≈ 1e-6). That is the honest number, and
   it is smaller than it sounds: it takes 30 drops between the same two
   neighbours, never merely 30 drags. Any drop elsewhere lands in a fresh gap
   with its own 30. The real double has ~40 more halvings in it at this
   magnitude; the margin is deliberate, since a rank that silently ties with its
   neighbour is a far worse failure than an occasional renumber. */
const MIN_RANK_GAP = 1e-6;

/**
 * A rank strictly between two neighbours, or null when the gap is too small to
 * split and the list needs renumbering.
 *
 * `prev`/`next` are the ranks of the rows the task is being dropped BETWEEN;
 * null means "no neighbour on that side", i.e. the head or tail of the list.
 */
export function rankBetween(prev: number | null, next: number | null): number | null {
  if (prev === null && next === null) return 0;
  if (prev === null) return next! - RANK_STEP;
  if (next === null) return prev + RANK_STEP;
  if (next - prev < MIN_RANK_GAP) return null;
  return prev + (next - prev) / 2;
}

/** Rank every row in `ordered` afresh, `RANK_STEP` apart. The escape hatch for
   when `rankBetween` returns null — it writes the whole list, which is the
   O(n) sync cost fractional ranking exists to avoid, so it is deliberately the
   rare path rather than the normal one. */
export function renumber(ordered: Rankable[]): { id: string; sortOrder: number }[] {
  return ordered.map((t, i) => ({ id: t.id, sortOrder: i * RANK_STEP }));
}

/** Anything carrying a manual rank. Tasks and habits both do, and they share
   every line of the machinery below — only the lists differ. */
export interface Rankable {
  id: string;
  sortOrder?: number;
}

/** The rank of `t`, tolerating a row that predates the column. */
export function rankOf(t: Rankable): number {
  return t.sortOrder ?? 0;
}

/**
 * Where a task should land when moved to index `to` of `list` (the list as it
 * reads BEFORE the move, including the task itself at `from`).
 *
 * Returns the single {id, sortOrder} to write, or a full renumbering when the
 * target gap can no longer be split. Callers persist whichever they get, so the
 * rare renumber costs a batch and the normal move costs one op.
 */
export function moveWithin(
  list: Rankable[],
  from: number,
  to: number,
): { id: string; sortOrder: number }[] {
  if (from === to || from < 0 || from >= list.length) return [];
  const moved = list[from]!;
  // the list as it will read once the task is out of its old slot — the
  // neighbours at the destination are read from THIS, not from the original,
  // or dropping downward lands one slot short
  const without = list.filter((_, i) => i !== from);
  const clamped = Math.max(0, Math.min(without.length, to));
  const prev = clamped > 0 ? rankOf(without[clamped - 1]!) : null;
  const next = clamped < without.length ? rankOf(without[clamped]!) : null;
  const rank = rankBetween(prev, next);
  if (rank !== null) return [{ id: moved.id, sortOrder: rank }];
  // the gap collapsed — renumber the list in its post-move order
  const after = [...without.slice(0, clamped), moved, ...without.slice(clamped)];
  return renumber(after);
}

/** The rank for a task landing in a BACKLOG: one step above that list's current
   head, so it arrives at the top. New work is what you just thought of, and
   burying it under a long triage queue is the wrong default — Todoist and Things
   both surface a fresh item rather than appending it.

   Scoped, because the backlogs are: an unfiled task joins the global queue, a
   filed one joins its project's (see `backlog`). */
export function rankForBacklog(s: AppState, projectId: string | null): number {
  const list = backlog(s, projectId ? { kind: 'project', id: projectId } : null);
  const head = list.reduce<number | null>(
    (min, t) => (min === null || rankOf(t) < min ? rankOf(t) : min),
    null,
  );
  return head === null ? 0 : head - RANK_STEP;
}

/**
 * The rank for a task landing on a DAY: the slot its time of day implies.
 *
 * This is what keeps a day nobody has arranged reading as a chronological
 * agenda even though the list now sorts by rank — an 09:00 task added to a day
 * lands above the 14:00 one, and an all-day task lands at the end. Once you drag
 * something the arrangement is yours, and nothing re-sorts it back.
 *
 * `exclude` skips a task already in the list — a task being MOVED within a day
 * must not be positioned against its own current slot.
 */
export function rankForDay(
  s: AppState,
  day: string,
  remindAt: number | null,
  exclude?: string,
): number {
  const list = tasksForDay(s, day).filter((t) => t.id !== exclude);
  const probe = { remindAt } as Task;
  // the first task the newcomer should sit BEFORE, by the old agenda rule
  const idx = list.findIndex((t) => byTimeOfDay(probe, t) < 0);
  const at = idx === -1 ? list.length : idx;
  const prev = at > 0 ? rankOf(list[at - 1]!) : null;
  const next = at < list.length ? rankOf(list[at]!) : null;
  return (
    rankBetween(prev, next) ??
    // the gap collapsed and there is no list to renumber from here — land at the
    // end rather than tying, which a later drag can always fix
    (list.length ? rankOf(list[list.length - 1]!) + RANK_STEP : 0)
  );
}

/** The rank a task should take when it MOVES to `day` (null = a backlog). */
export function rankForMove(s: AppState, id: string, day: string | null): number {
  const t = s.tasks.find((x) => x.id === id);
  if (!t) return 0;
  return day === null
    ? rankForBacklog(s, t.projectId ?? null)
    : rankForDay(s, day, t.remindAt ?? null, id);
}

/* ---------- project transforms ---------- */

export function createProject(s: AppState, id: string, name: string, hue: HueName): AppState {
  return { ...s, projects: [...s.projects, { id, name, hue, archivedAt: null }] };
}

export function updateProject(
  s: AppState,
  id: string,
  patch: { name?: string; hue?: HueName },
): AppState {
  return { ...s, projects: s.projects.map((l) => (l.id === id ? { ...l, ...patch } : l)) };
}

/** Archive / un-archive — the project leaves (or returns to) the picker. Its tasks
   are untouched either way: they keep their days, their history and their
   colour, which is the whole point of archive over delete. */
export function setProjectArchived(s: AppState, id: string, archived: boolean): AppState {
  const at = archived ? new Date().toISOString() : null;
  return { ...s, projects: s.projects.map((l) => (l.id === id ? { ...l, archivedAt: at } : l)) };
}

/** Delete a project and UNFILE its tasks — never delete them. They stay on whatever
   days they had and simply stop being filed, which mirrors what the server does
   in the same transaction (see backend ListRepository.delete). */
export function deleteProject(s: AppState, id: string): AppState {
  return {
    ...s,
    projects: s.projects.filter((l) => l.id !== id),
    tasks: s.tasks.map((t) => (t.projectId === id ? { ...t, projectId: null } : t)),
  };
}

/* ---------- tag transforms ---------- */

/** Create a tag, or return the state unchanged when one already carries that
   name (case-folded). Idempotent to match the server, which treats a repeat
   create as "you meant the existing one" rather than a conflict — fast entry
   types tags into being, so the two have to agree. */
export function createTag(s: AppState, id: string, name: string, hue: HueName): AppState {
  if (tagByName(s, name)) return s;
  return { ...s, tags: [...s.tags, { id, name, hue }] };
}

export function updateTag(s: AppState, id: string, patch: { name?: string; hue?: HueName }): AppState {
  return { ...s, tags: s.tags.map((t) => (t.id === id ? { ...t, ...patch } : t)) };
}

/** Delete a tag and STRIP it from its tasks — never delete them. They keep their
   day, their project and their history and simply lose one facet, mirroring what
   the server does in the same transaction (see backend TagRepository.delete). */
export function deleteTag(s: AppState, id: string): AppState {
  return {
    ...s,
    tags: s.tags.filter((t) => t.id !== id),
    tasks: s.tasks.map((t) =>
      tagIdsOf(t).includes(id) ? { ...t, tagIds: tagIdsOf(t).filter((x) => x !== id) } : t,
    ),
  };
}

/** Apply new ranks to tasks by id — what a reorder commits locally. Takes the
   same {id, sortOrder} list the caller queues, so the optimistic state and the
   ops in the outbox can't disagree. */
export function applyRanks(s: AppState, ranks: { id: string; sortOrder: number }[]): AppState {
  if (ranks.length === 0) return s;
  const by = new Map(ranks.map((r) => [r.id, r.sortOrder]));
  return {
    ...s,
    tasks: s.tasks.map((t) => (by.has(t.id) ? { ...t, sortOrder: by.get(t.id)! } : t)),
  };
}

/** The habit-list counterpart. Separate from `applyRanks` rather than scanning
   both collections: ids are unique across them in practice, but a function that
   silently touches whichever table happens to match is the kind of thing that
   goes wrong quietly. */
export function applyHabitRanks(
  s: AppState,
  ranks: { id: string; sortOrder: number }[],
): AppState {
  if (ranks.length === 0) return s;
  const by = new Map(ranks.map((r) => [r.id, r.sortOrder]));
  return {
    ...s,
    habits: s.habits.map((h) => (by.has(h.id) ? { ...h, sortOrder: by.get(h.id)! } : h)),
  };
}

/** The rank a NEW habit takes: the end of the list. Unlike a captured task —
   which goes to the top of its backlog, being the thing you just thought of — a
   habit is a standing commitment joining a set you already keep, so appending is
   what every habit app does and what the old array-append did. */
export function rankForNewHabit(s: AppState): number {
  const tail = s.habits.reduce<number | null>(
    (max, h) => (max === null || rankOf(h) > max ? rankOf(h) : max),
    null,
  );
  return tail === null ? 0 : tail + RANK_STEP;
}

/** Habits in the user's own order. */
export function orderedHabits(s: AppState): Habit[] {
  return [...s.habits].sort((a, b) => rankOf(a) - rankOf(b));
}

/* ---------- selectors ---------- */

export type ItemRef = { item: Habit | Task; project: 'habits' | 'tasks' };

export function findItem(s: AppState, id: string): ItemRef | null {
  const h = s.habits.find((x) => x.id === id);
  if (h) return { item: h, project: 'habits' };
  const t = s.tasks.find((x) => x.id === id);
  if (t) return { item: t, project: 'tasks' };
  return null;
}

/** The id pinned for a day, or null. Defensively hides a pin that points at a
   missing task/habit (a dangling id, e.g. a deleted item) or at a task that has
   since been completed or skipped — those are no longer a "do this first". The
   mock keeps pinnedByDay clean by unpinning on those transitions; this guard
   makes the same true for the API layer, whose server-side pins can dangle. */
export function pinnedIdFor(s: AppState, day: string): string | null {
  const id = s.pinnedByDay[day] ?? null;
  if (!id) return null;
  const rec = findItem(s, id);
  if (!rec) return null; // dangling — item gone
  if (rec.project === 'tasks') {
    const t = rec.item as Task;
    if (t.done || t.skipped) return null; // resolved — drop the pin from view
    // moved to a later day (a reschedule, or a repeating task that advanced past
    // this day) — it isn't this day's "do this first" any more. A task still on an
    // *earlier* day stays pinned: pinning an overdue task onto today is deliberate.
    if (t.scheduledFor !== null && t.scheduledFor > day) return null;
  }
  return id;
}

export function pinnedItem(s: AppState, day: string): ItemRef | null {
  const id = pinnedIdFor(s, day);
  return id ? findItem(s, id) : null;
}

/** live, non-skipped, incomplete tasks — the base for every task view. A
   repeating task is one of these like any other row: it just moves itself along
   when resolved, so it appears exactly once, on its next day. */
export function activeTasks(s: AppState): Task[] {
  return s.tasks.filter((t) => !t.done && !t.skipped);
}

/**
 * Order two tasks by time of day: timed first, earliest first, then all-day.
 *
 * NO LONGER THE SORT for a day's list — that is `sortOrder` now, so a day can be
 * arranged by hand (see docs/task-ordering.md). This survives as the rule that
 * SEEDS a rank when a task is scheduled, so a day nobody has touched still reads
 * as a chronological agenda; and as the tiebreak for two tasks that landed on
 * the same rank.
 *
 * Returns 0 for two untimed tasks so a stable sort leaves their order alone.
 */
export function byTimeOfDay(a: Task, b: Task): number {
  const at = a.remindAt ?? null;
  const bt = b.remindAt ?? null;
  if (at === bt) return 0;
  if (at === null) return 1;
  if (bt === null) return -1;
  return at - bt;
}

/** A day's tasks, in the user's own order. Time still shows on the row and still
   fires the reminder; it just no longer dictates the sequence — see
   docs/task-ordering.md for why the agenda rule became a seeding rule. */
export function tasksForDay(s: AppState, day: string): Task[] {
  return activeTasks(s)
    .filter((t) => t.scheduledFor === day)
    .sort((a, b) => rankOf(a) - rankOf(b) || byTimeOfDay(a, b));
}

export function doneForDay(s: AppState, day: string): Task[] {
  return s.tasks
    .filter((t) => t.done && t.doneAt === day)
    .sort((a, b) => b.lastTouched - a.lastTouched);
}

export function skippedForDay(s: AppState, day: string): Task[] {
  return s.tasks
    .filter((t) => t.skipped && t.skippedAt === day)
    .sort((a, b) => b.lastTouched - a.lastTouched);
}

/** Incomplete tasks whose day has passed — oldest first. A repeating task shows
   up here as the single row it is, so a week of missed days is one card, not
   seven (and resolving it jumps the chain ahead of the misses). */
export function overdue(s: AppState): Task[] {
  return activeTasks(s)
    .filter((t) => t.scheduledFor !== null && t.scheduledFor < todayStr())
    // Orderable like every other pile. Oldest-first only SEEDS it (and breaks
    // ties): this is a triage queue, so which one you deal with first is a
    // judgement, not a function of how long it has been sitting. Each row is
    // stamped with the day it slipped from, so nothing is hidden by the reorder.
    .sort((a, b) => rankOf(a) - rankOf(b) || (a.scheduledFor! < b.scheduledFor! ? -1 : 1));
}

/**
 * Undated, unresolved tasks — in the user's own manual order.
 *
 * The sort used to be `lastTouched` (recency), which is noise on the one surface
 * where you are choosing what to pull into a day. `sortOrder` is the answer to
 * that question; recency only breaks ties, which matters for rows written before
 * manual ordering existed (they all carry rank 0) and for two tasks that landed
 * on the same rank through a merge.
 *
 * Scoped by project, because a DATE is what makes a task global:
 *
 *   • dated (today, future, or overdue) → belongs to the day views, whatever
 *     project it's in; you committed it to a day and the day is shared.
 *   • undated + filed → belongs to its PROJECT and nowhere else. A project's undated
 *     items are the project's contents, not your triage queue: forty grocery
 *     items or a packing project would drown the global backlog, which exists to
 *     answer "what still needs a day?".
 *   • undated + unfiled → the global backlog, which is then exactly that.
 *
 * So `backlog()` with no project returns only UNFILED tasks; pass a `projectId` for
 * that project's own. See docs/projects.md.
 */
export function backlog(s: AppState, scope?: Scope | null): Task[] {
  return activeTasks(s)
    .filter((t) => t.scheduledFor === null)
    .filter((t) => {
      // Unscoped, the backlog is the UNFILED undated tasks only — a date is what
      // makes a task global, so a filed task with no day belongs to its project
      // rather than to your triage queue (see docs/projects.md).
      //
      // A TAG scope reads differently on purpose: it means "the undated tasks
      // carrying this tag", filed or not. A tag is a facet rather than a place,
      // so there is no "unfiled" counterpart for it to exclude.
      if (!scope) return !t.projectId;
      return scope.kind === 'project' ? t.projectId === scope.id : tagIdsOf(t).includes(scope.id);
    })
    .sort((a, b) => rankOf(a) - rankOf(b) || b.lastTouched - a.lastTouched);
}

/** Incomplete tasks scheduled today or later — soonest first, and within a day
   chronological (timed before all-day). A repeating task contributes its next
   occurrence and nothing more (one card per series, as in Todoist / Things
   "Upcoming"), because later slots simply don't exist yet. */
export function upcoming(s: AppState): Task[] {
  const today = todayStr();
  return activeTasks(s)
    .filter((t) => t.scheduledFor !== null && t.scheduledFor >= today)
    .sort((a, b) => {
      // the DAY always leads — Upcoming is grouped by day, and a rank must never
      // pull a task out of its own group
      if (a.scheduledFor! !== b.scheduledFor!) return a.scheduledFor! < b.scheduledFor! ? -1 : 1;
      return rankOf(a) - rankOf(b) || byTimeOfDay(a, b) || b.lastTouched - a.lastTouched;
    });
}

/** Per-day activity counts over [start, end] (inclusive), keyed "YYYY-MM-DD" —
   what the Today strip's dots read from, so a day earns a dot when anything
   lands on it. Two sources, the same ones the day selectors draw from: tasks (on
   the day they resolved — doneAt/skippedAt — or are scheduled for) and habit
   check-ins. A repeating task dots only its next day; the days it already ran are
   dotted by the history rows it left behind. */
export function dayActivityCounts(s: AppState, start: string, end: string): Record<string, number> {
  const counts: Record<string, number> = {};
  const bump = (day: string | null | undefined) => {
    if (day && day >= start && day <= end) counts[day] = (counts[day] ?? 0) + 1;
  };
  for (const t of s.tasks) {
    bump(t.done ? t.doneAt : t.skipped ? t.skippedAt : t.scheduledFor);
  }
  // a habit check-in also makes a day active
  for (const days of Object.values(s.checkins)) {
    for (const day of Object.keys(days)) bump(day);
  }
  return counts;
}

/** True when today has nothing left: every habit DUE today is checked in and no
   incomplete tasks remain. A habit on a planned rest day doesn't hold the day
   open — otherwise a Mon/Wed/Fri habit would make Tuesday impossible to finish.

   Reads due-ness and done-ness from the schedule and the check-in history rather
   than the `dueToday`/`done` fields on `s.habits`, which are derived for whatever
   day was last *viewed* and so can be stale relative to today. Due-ness never
   depends on where the week starts (see shared/habits/schedule.ts `isDueOn`), so
   this needs no `weekStartsOn`. */
export function isAllDone(s: AppState): boolean {
  const today = todayStr();
  const habitsDone = s.habits.every(
    (h) =>
      !isDueOn(h.schedule, today, h.startsOn) ||
      (s.checkins[h.id]?.[today]?.completed ?? false),
  );
  const todaysLeft = tasksForDay(s, today).length > 0;
  return habitsDone && !todaysLeft;
}

/* ---------- transforms ---------- */

/** Set a task's scheduled day (null = backlog) and bump its touch time. If the
   task was pinned on its old day, the pin follows it to the new day so a
   rescheduled pin never strands a card on a day the task has left. */
export function setTaskDay(s: AppState, id: string, day: string | null): AppState {
  const from = s.tasks.find((t) => t.id === id)?.scheduledFor ?? null;
  // Re-ranked into the list it is arriving in. One rank is stored per task and
  // read relative to whatever list is showing, so a task that kept its old rank
  // would drop into an arbitrary slot of the new one — jarring for a "Tomorrow"
  // replan, which should land somewhere you'd expect. Skipped when the day
  // hasn't actually changed.
  const rank = from === day ? null : rankForMove(s, id, day);
  const tasks = s.tasks.map((t) =>
    t.id === id
      ? {
          ...t,
          scheduledFor: day,
          ...(rank === null ? {} : { sortOrder: rank }),
          // moving to the backlog drops the time of day — it has no day left to
          // resolve against, and keeping it would silently re-arm the reminder
          // the moment the task got scheduled again (mirrors the server's
          // TaskService.rescheduleTask)
          ...(day === null ? { remindAt: null } : {}),
          lastTouched: Date.now(),
        }
      : t,
  );
  let pinnedByDay = s.pinnedByDay;
  if (from !== null && pinnedByDay[from] === id) {
    pinnedByDay = { ...pinnedByDay, [from]: null };
    if (day !== null) pinnedByDay[day] = id;
  }
  return { ...s, tasks, pinnedByDay };
}

/* ---------- the chain step (repeating tasks) ----------
   Mirrors the backend TaskService.advanceChain so the optimistic state matches
   what the server answers with: resolving a repeating task snapshots the day it
   was on into a plain history row and moves the live row to its next day, with a
   fresh unchecked copy of its sub-steps. */

/** The resolution being recorded on the day the task is leaving. */
type Resolution = {
  done: boolean;
  doneAt: string | null;
  skipped: boolean;
  skippedAt: string | null;
};

/** Advance a repeating task, returning the new state — or null when there's
   nothing to advance (the task doesn't repeat, or its rule is exhausted), in
   which case the caller resolves it in place and the series ends. `snapshotId` is
   the id the history row takes: a temp id the API layer swaps for the server's. */
export function advanceChain(
  s: AppState,
  task: Task,
  resolution: Resolution,
  snapshotId: string,
): AppState | null {
  if (!task.rrule) return null;
  const today = todayStr();
  const current = task.scheduledFor ?? today;
  const next = nextChainDay(task.rrule, current, today);
  if (!next) return null;

  const snapshot: Task = {
    ...task,
    id: snapshotId,
    rrule: null,
    scheduledFor: current,
    subSteps: task.subSteps.map((st) => ({ ...st })),
    lastTouched: Date.now(),
    ...resolution,
  };
  const tasks = s.tasks.map((t) =>
    t.id === task.id
      ? {
          ...t,
          scheduledFor: next,
          subSteps: t.subSteps.map((st) => ({ t: st.t, done: false })),
          lastTouched: Date.now(),
        }
      : t,
  );
  return {
    ...s,
    tasks: [...tasks, snapshot],
    // the day just resolved no longer has a "do this first" — the row has moved on
    pinnedByDay: unpinEverywhere(s.pinnedByDay, task.id),
  };
}

/** The id a locally-resolved occurrence's history row takes when the caller has
   no temp id of its own — stable per (task, day) so a double-tap can't fork it. */
function localSnapshotId(task: Task, day: string): string {
  return `${task.id}@${day}`;
}

/* ---------- per-day habit state (derived from check-in history) ---------- */

/** A habit definition with its live state derived for a specific day from the
   check-in history — so any day shows its real state, past or present.

   The scoring itself is `deriveCadenceState` from @habishine/shared, the SAME
   function the backend runs (backend/src/services/habit-consistency.ts). One
   engine, so an optimistic local update and the server's response can't disagree
   — and an unscheduled day stays neutral in both. */
export function habitForDay(
  s: AppState,
  habit: Habit,
  day: string,
  weekStartsOn: number,
): Habit {
  const days = s.checkins[habit.id] ?? {};
  const ck = days[day];
  const completedDays = Object.keys(days).filter((d) => days[d]?.completed);
  const cadence = deriveCadenceState({
    schedule: habit.schedule,
    startsOn: habit.startsOn,
    completedDays,
    today: day,
    weekStartsOn,
  });
  return {
    ...habit,
    ...cadence,
    progress: ck?.progress ?? 0,
    done: ck?.completed ?? false,
  };
}

/** Every habit with its state derived for `day` — the home screen's habit project
   for the selected calendar day. Habits NOT due on `day` are kept in the project
   (with `dueToday: false`) rather than filtered out here: the Today screen hides
   them, while the habits screen and detail views still need to show them. */
/** A day's habits, scored for that day, in the user's own order — the same order
   the library shows, so a habit sits in the same place wherever you meet it. */
export function habitsForDay(s: AppState, day: string, weekStartsOn: number): Habit[] {
  return orderedHabits(s).map((h) => habitForDay(s, h, day, weekStartsOn));
}

/* ---------- weekly review stats (derived from history) ---------- */

/** The seven "YYYY-MM-DD" day strings of the week beginning at `weekStart`. */
export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDaysStr(weekStart, i));
}

/** A habit's completion on a single day as a 0..1 value: 1 when the day was
   kept, else the fraction of that day's target logged (0 when nothing). The
   per-cell shade for the heatmap and any single-day progress read. */
export function dayCompletion(s: AppState, habit: Habit, day: string): number {
  const ck = (s.checkins[habit.id] ?? {})[day];
  if (!ck) return 0;
  return ck.completed ? 1 : Math.min(1, ck.progress / Math.max(1, habit.target));
}

export interface WeekStats {
  /** per-habit completion fraction (kept days ÷ days the cadence asked for),
     aligned to `state.habits`. 0 when the week asked for nothing (a habit that
     hadn't started yet), so an untouched week reads empty rather than as a miss. */
  pcts: number[];
  /** tasks completed within the week (doneAt within the 7 days) */
  tasksDone: number;
  /** total habit check-ins — kept days summed across all habits in the week */
  checkIns: number;
  /** the week's completed rows across every project, most recent first — mirrors
     ProjectWeekStats.completed so the unscoped Review can offer the same
     bring-back/delete actions on a task it isn't filtering by project. */
  completed: Task[];
}

/** Real weekly stats for the week beginning at `weekStart` ("YYYY-MM-DD"),
   derived from check-in history and task completion days — no server round-trip;
   the client already holds this from the full sync. Weeks the client hasn't
   synced simply read as empty (0), never fabricated.

   Each habit's percentage is measured against its OWN cadence for that week, not
   against seven days: a Mon/Wed/Fri habit kept on all three of its days is 100%,
   and a "3 days a week" habit needs any 3. Dividing by 7 regardless would have
   capped every cadenced habit well under full and read as failure. */
export function weekStats(s: AppState, weekStart: string): WeekStats {
  const days = weekDays(weekStart);
  const daySet = new Set(days);
  const weekEnd = days[days.length - 1]!;
  let checkIns = 0;
  const pcts = s.habits.map((h) => {
    const ck = s.checkins[h.id] ?? {};
    const kept = days.reduce((n, d) => n + (ck[d]?.completed ? 1 : 0), 0);
    checkIns += kept;
    // what the cadence asked for in this week, clipped at the habit's start day
    const asked = scheduledCount(h.schedule, weekStart, weekEnd, h.startsOn);
    // a quota can be over-served (4 days on a 3-a-week habit) — cap at full
    return asked > 0 ? Math.min(1, kept / asked) : 0;
  });
  const completed = s.tasks
    .filter((t) => t.done && t.doneAt != null && daySet.has(t.doneAt))
    .sort((a, b) => b.lastTouched - a.lastTouched);
  return { pcts, tasksDone: completed.length, checkIns, completed };
}

/** Toggle a habit's check-in for `day`: log one rep, complete the day at target,
   or clear an already-completed day. `weekStartsOn` re-derives the habit's cadence
   state (the live week's "2 of 3" badge) — see habitForDay. */
export function checkInHabit(
  s: AppState,
  id: string,
  day: string,
  weekStartsOn: number,
): { state: AppState; celebrated: boolean } {
  const habit = s.habits.find((h) => h.id === id);
  if (!habit) return { state: s, celebrated: false };

  // Read the target day's current state from history and apply the
  // toggle/increment there, then re-derive TODAY's state for the habit array
  // (completing a past day can even extend today's run).
  const days = { ...(s.checkins[id] ?? {}) };
  const cur = days[day] ?? { progress: 0, completed: false };
  if (cur.completed) {
    // a completed day toggles back off — clear it
    delete days[day];
  } else if (habit.target > 1 && cur.progress + 1 < habit.target) {
    // numeric target, not there yet — log one
    days[day] = { progress: cur.progress + 1, completed: false };
  } else {
    // reached the target (or a binary habit) — record the day done
    const ck: DayCheckin = { progress: habit.target, completed: true };
    days[day] = ck;
  }
  const checkins = { ...s.checkins, [id]: days };
  const withCk: AppState = { ...s, checkins };
  const today = todayStr();
  const habits = s.habits.map((h) => (h.id === id ? habitForDay(withCk, h, today, weekStartsOn) : h));
  const next: AppState = { ...withCk, habits };
  return { state: next, celebrated: isAllDone(next) };
}

/** Resolve a task as done (or re-open one already done). A repeating task
   advances the chain (see advanceChain) using `snapshotId` for the history row it
   leaves behind. Habits check in through checkInHabit instead — a habit's day is
   a per-day record, not a one-way resolution. */
export function completeTask(
  s: AppState,
  id: string,
  snapshotId?: string,
): { state: AppState; celebrated: boolean } {
  const task = s.tasks.find((t) => t.id === id);
  if (!task) return { state: s, celebrated: false };

  if (!task.done) {
    // repeating: record this day as done history and move the row to its next slot
    const resolvedDay = task.scheduledFor ?? todayStr();
    const advanced = advanceChain(
      s,
      task,
      { done: true, doneAt: resolvedDay, skipped: false, skippedAt: null },
      snapshotId ?? localSnapshotId(task, resolvedDay),
    );
    if (advanced) return { state: advanced, celebrated: isAllDone(advanced) };
  }

  const next: AppState = { ...s };
  next.habits = s.habits.map((h) => ({ ...h }));
  next.tasks = s.tasks.map((t) => ({ ...t }));
  const item = next.tasks.find((t) => t.id === id)!;
  if (item.done) {
    // un-complete: re-open the task and clear its done day
    item.done = false;
    item.doneAt = null;
    return { state: next, celebrated: false };
  }
  item.done = true;
  // record completion on the task's own day (backlog → today) so it lands in
  // that day's "Done" group, and clear it from any day that pinned it.
  item.doneAt = item.scheduledFor ?? todayStr();
  next.pinnedByDay = unpinEverywhere(next.pinnedByDay, id);
  return { state: next, celebrated: isAllDone(next) };
}

/** Pin any habit or task into `day`'s slot, replacing whatever it had. Passing
   the id already pinned for that day clears it. A backlog task (no day) is
   scheduled onto `day` so its pin card and its project row share a day. */
export function togglePin(s: AppState, id: string, day: string): AppState {
  const clearing = s.pinnedByDay[day] === id;
  const pinnedByDay = { ...s.pinnedByDay, [day]: clearing ? null : id };
  const task = s.tasks.find((t) => t.id === id);
  const needsDay = !clearing && task && task.scheduledFor === null;
  const base = needsDay ? setTaskDay(s, id, day) : s;
  return { ...base, pinnedByDay };
}

export function toggleStep(s: AppState, taskId: string, index: number): AppState {
  return {
    ...s,
    tasks: s.tasks.map((t) =>
      t.id === taskId
        ? {
            ...t,
            lastTouched: Date.now(),
            subSteps: t.subSteps.map((step, i) => (i === index ? { ...step, done: !step.done } : step)),
          }
        : t,
    ),
  };
}

/** Bump the last of today's tasks back to the backlog (no day). Returns the
   moved task's id (or null when today is empty) so a caller can persist it. */
export function moveLastToLater(s: AppState): { state: AppState; movedId: string | null } {
  const todays = activeTasks(s).filter((t) => t.scheduledFor === todayStr());
  const last = todays[todays.length - 1];
  if (!last) return { state: s, movedId: null };
  return { state: setTaskDay(s, last.id, null), movedId: last.id };
}

export function rescheduleTask(s: AppState, id: string, day: string | null): AppState {
  return setTaskDay(s, id, day);
}

/** Patch a task's own attributes. `rrule` starts/changes the cadence from the
   task's current day; `null` stops it repeating (the row survives as a one-off).
   `remindAt` sets the time of day; `null` makes it all-day again. `projectId` files
   the task under a project; `null` unfiles it. */
export function updateTask(
  s: AppState,
  id: string,
  patch: {
    name?: string;
    hue?: HueName;
    projectId?: string | null;
    /** REPLACES the tag set (absent leaves it alone) — the same contract the
        server's PATCH has, so the optimistic row and its echo agree. */
    tagIds?: string[];
    rrule?: string | null;
    remindAt?: number | null;
  },
): AppState {
  return {
    ...s,
    tasks: s.tasks.map((t) => (t.id === id ? { ...t, ...patch, lastTouched: Date.now() } : t)),
  };
}

/** Patch a habit's own attributes. Lowering `target` below the reps already
   logged clamps them to it and completes the day — the lower bar is met.

   A `schedule` change is normalized and the habit re-scored against the new
   cadence for today, so the rest-day state and the week badge move with the edit
   instead of waiting for the server. Check-in history is never rewritten: the
   same days simply count against a different yardstick. */
export function updateHabit(
  s: AppState,
  id: string,
  patch: {
    name?: string;
    emoji?: string;
    hue?: HueName;
    target?: number;
    schedule?: HabitSchedule;
  },
  weekStartsOn: number,
): AppState {
  return {
    ...s,
    habits: s.habits.map((h) => {
      if (h.id !== id) return h;
      const target = patch.target != null ? Math.max(1, Math.round(patch.target)) : h.target;
      const progress = Math.min(h.progress, target);
      const edited: Habit = {
        ...h,
        ...patch,
        schedule: patch.schedule ? normalizeSchedule(patch.schedule) : h.schedule,
        target,
        progress,
        done: target > 1 ? progress >= target : h.done,
      };
      // Take the cadence read off the new schedule but keep the day progress
      // clamped above (habitForDay reads it back from the check-in row).
      const scored = habitForDay(s, edited, todayStr(), weekStartsOn);
      return { ...scored, progress: edited.progress, done: edited.done };
    }),
  };
}

export function deleteHabit(s: AppState, id: string): AppState {
  return {
    ...s,
    habits: s.habits.filter((h) => h.id !== id),
    pinnedByDay: unpinEverywhere(s.pinnedByDay, id),
  };
}

/** Put a deleted habit back. `index` is vestigial: the list is ordered by
   `sortOrder` now, and the snapshot carries the rank the habit had, so it
   returns to its own place rather than to a position in the array. Kept in the
   signature because the undo toast still passes it, and splicing near the right
   spot keeps the pre-sort array tidy. */
export function restoreHabit(s: AppState, habit: Habit, index: number): AppState {
  if (s.habits.some((h) => h.id === habit.id)) return s;
  const habits = [...s.habits];
  habits.splice(Math.max(0, Math.min(index, habits.length)), 0, habit);
  return { ...s, habits };
}

export function undoDone(s: AppState, id: string): AppState {
  return {
    ...s,
    tasks: s.tasks.map((t) =>
      t.id === id ? { ...t, done: false, doneAt: null, lastTouched: Date.now() } : t,
    ),
  };
}

/** Skip a task. A repeating one advances the chain, recording the skipped day as
   history — one skipped day never ends the series. */
export function skipTask(s: AppState, id: string, snapshotId?: string): AppState {
  const task = s.tasks.find((t) => t.id === id);
  if (!task) return s;
  const day = task.scheduledFor ?? todayStr();
  const advanced = advanceChain(
    s,
    task,
    { skipped: true, skippedAt: day, done: false, doneAt: null },
    snapshotId ?? localSnapshotId(task, day),
  );
  if (advanced) return advanced;
  return {
    ...s,
    tasks: s.tasks.map((t) =>
      t.id === id ? { ...t, skipped: true, skippedAt: day, lastTouched: Date.now() } : t,
    ),
    pinnedByDay: unpinEverywhere(s.pinnedByDay, id),
  };
}

export function unskipTask(s: AppState, id: string): AppState {
  return {
    ...s,
    tasks: s.tasks.map((t) =>
      t.id === id ? { ...t, skipped: false, skippedAt: null, lastTouched: Date.now() } : t,
    ),
  };
}

export function deleteTask(s: AppState, id: string): AppState {
  return {
    ...s,
    tasks: s.tasks.filter((t) => t.id !== id),
    pinnedByDay: unpinEverywhere(s.pinnedByDay, id),
  };
}

export function restoreTask(s: AppState, task: Task): AppState {
  return s.tasks.some((t) => t.id === task.id) ? s : { ...s, tasks: [...s.tasks, task] };
}

/** Triage a task to a destination. Moving a repeating task re-anchors its cadence
   (the rule is anchored on its day); resolving one advances the chain, so `Done`
   and `Skipped` go through the same helpers the row actions use. */
export function replanTo(
  s: AppState,
  id: string,
  where: ReplanWhere,
  snapshotId?: string,
): AppState {
  const t = s.tasks.find((x) => x.id === id);
  if (!t) return s;
  switch (where) {
    case 'Today':
      return setTaskDay(s, id, todayStr());
    case 'Tomorrow':
      return setTaskDay(s, id, addDaysStr(todayStr(), 1));
    case 'This weekend':
      return setTaskDay(s, id, nextWeekendStr());
    case 'Someday':
      return setTaskDay(s, id, null);
    case 'Done':
      return t.done ? s : completeTask(s, id, snapshotId).state;
    case 'Skipped':
      return skipTask(s, id, snapshotId);
  }
}

/** Build the habit or task a capture creates, inserting it under `id` (a temp id
   for the API layer, a local id for the mock). `day` is the destination day for
   a scheduled task; habits ignore it (a habit begins today, whichever day is
   being viewed). */
export function saveCapture(
  s: AppState,
  id: string,
  name: string,
  cap: CaptureState,
  day: string,
  weekStartsOn: number,
): AppState {
  if (cap.habit) {
    // A habit begins the day it's created — days before `startsOn` predate it
    // and are never counted as misses.
    const startsOn = todayStr();
    const schedule = normalizeSchedule(cap.schedule);
    const h: Habit = {
      id,
      name,
      emoji: cap.emoji,
      hue: cap.hue,
      target: Math.max(1, cap.target ?? 1),
      schedule,
      startsOn,
      progress: 0,
      done: false,
      // Derived from the same function that scores every other habit, rather
      // than hand-written zeroes, so a fresh habit can't read differently here
      // than it will on the next re-derive (`rate` null, no numbers to protect).
      ...deriveCadenceState({ schedule, startsOn, completedDays: [], today: startsOn, weekStartsOn }),
    };
    return { ...s, habits: [...s.habits, { ...h, sortOrder: rankForNewHabit(s) }] };
  }
  // A recurring capture is still one task row — it just carries an `rrule`,
  // anchored on the day it's scheduled for. It needs a day to repeat from, so a
  // repeating capture lands on `day` even if "today" wasn't toggled.
  const rrule = cap.recurrence ? presetToRRule(cap.recurrence) : null;
  const scheduledFor = cap.today || rrule ? day : null;
  const base: Task = {
    id,
    name,
    hue: cap.hue,
    // filing is optional and never blocks capture — an unfiled task is fully
    // placed on the date axis already (Today or Backlog)
    projectId: cap.projectId,
    tagIds: cap.tagIds,
    done: false,
    scheduledFor,
    // a time needs a day to resolve to an instant, so a backlog capture drops it
    // (same rule the server applies in TaskService.createTask)
    remindAt: scheduledFor ? cap.remindAt : null,
    rrule,
    // Ranked by DESTINATION: a task landing on a day takes the slot its time
    // implies (so an untouched day still reads chronologically); one landing in
    // a backlog goes to the top of it.
    sortOrder: scheduledFor
      ? rankForDay(s, scheduledFor, scheduledFor ? cap.remindAt : null)
      : rankForBacklog(s, cap.projectId),
    lastTouched: Date.now(),
    subSteps: [],
  };
  return { ...s, tasks: [...s.tasks, base] };
}
